import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AuthFormContainer from '@/features/auth/signup/components/AuthFormContainer';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { emailVerificationRoute } from '@/features/auth/signup/routes/emailVerificationRoute.ts';
import useSendEmailVerification from '@/features/auth/signup/hooks/useSendEmailVerification.ts';
import { queryClient } from '@/App.tsx';
import { userApiQueryKeys } from '@/features/dashboard/_root/api/userApi.queryKeys.ts';
import { readInvitationFromRedirect } from '@/features/public/acceptInvitation/utils/invitationFromRedirect.ts';
import { Description } from '@/components/ui/Title.tsx';
import {
  RATE_LIMIT_COOLDOWN_SECONDS,
  formatResendCooldown,
  isEmailVerificationRateLimited,
} from '@/features/auth/signup/utils/emailVerificationRateLimit.ts';

const RESEND_COOLDOWN_SECONDS = 30;

export const EmailVerificationPage = () => {
  const [cooldown, setCooldown] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { redirect } = emailVerificationRoute.useSearch();
  const invitation = readInvitationFromRedirect(redirect);
  const {
    mutate: sendVerification,
    isPending,
    isSuccess,
    isError,
    error,
  } = useSendEmailVerification();
  const isRateLimited = isEmailVerificationRateLimited(error);
  // Ta strona jest jedynym miejscem, które wysyła mail weryfikacyjny — rejestracja i ekran
  // nieudanej weryfikacji tylko tu kierują. Ref chroni przed drugą wysyłką przy podwójnym
  // uruchomieniu efektu w StrictMode i przy zmianie referencji `user` po reload().
  const autoSendStarted = useRef(false);

  const startRateLimitCooldown = (sendError: Error) => {
    if (isEmailVerificationRateLimited(sendError)) {
      setCooldown(RATE_LIMIT_COOLDOWN_SECONDS);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (!user || user.emailVerified || autoSendStarted.current) return;
    autoSendStarted.current = true;
    // `emailVerified` w SDK bywa nieświeże (weryfikacja w innej karcie) — odświeżamy, zanim
    // poprosimy backend o mail, który odrzuciłby go jako już zweryfikowany.
    const sendIfStillUnverified = async () => {
      await user.reload();
      if (user.emailVerified) return;
      sendVerification(undefined, {
        onSuccess: () => setCooldown(RESEND_COOLDOWN_SECONDS),
        onError: startRateLimitCooldown,
      });
    };
    void sendIfStillUnverified();
  }, [user, sendVerification]);

  useEffect(() => {
    const checkEmailVerification = async () => {
      await user?.reload();
      if (user?.emailVerified) {
        await user.getIdToken(true);
        // `me` pobrane przed weryfikacją nie ma zaproszeń (backend ukrywa je przed niezweryfikowanym
        // adresem), a ensureQueryData oddałby tę pustą kopię i wysłał zaproszonego do zakładania firmy.
        queryClient.removeQueries({ queryKey: userApiQueryKeys.me() });
        if (redirect) {
          window.location.href = redirect;
        } else {
          navigate({ to: '/auth/complete-profile' });
        }
      }
    };

    const interval = setInterval(checkEmailVerification, 500);
    return () => clearInterval(interval);
  }, [user, navigate, redirect]);

  const handleResendEmail = () => {
    if (!user || user.emailVerified) return;
    sendVerification(undefined, {
      onSuccess: () => {
        toast.success('Link został ponownie wysłany', {
          description: 'Sprawdź swoją skrzynkę pocztową.',
        });
        setCooldown(RESEND_COOLDOWN_SECONDS);
      },
      onError: startRateLimitCooldown,
    });
  };

  const statusMessage = isPending
    ? 'Wysyłamy link weryfikacyjny na podany adres email.'
    : isSuccess
      ? 'Wysłaliśmy link weryfikacyjny na podany adres email. Przejdź do poczty i kliknij w link, aby kontynuować.'
      : isRateLimited
        ? cooldown > 0
          ? 'Osiągnięto limit wysyłek. Sprawdź skrzynkę (także spam) — wcześniejszy link nadal działa. Kolejny mail wyślesz za kilka minut.'
          : 'Twój adres email wymaga potwierdzenia.'
        : isError
          ? 'Nie udało się wysłać linku weryfikacyjnego. Spróbuj ponownie przyciskiem poniżej.'
          : 'Twój adres email wymaga potwierdzenia.';

  return (
    <AuthFormContainer title="Potwierdź adres email">
      <div className="flex w-full flex-col items-start gap-8">
        <div className="w-full">
          <p className="mt-4 text-center text-[#6C6C93]">{statusMessage}</p>
          {invitation && (
            <Description className="mt-2 text-center">
              {invitation.companyName
                ? `Po potwierdzeniu adresu dołączysz do firmy ${invitation.companyName}`
                : 'Po potwierdzeniu adresu dołączysz do zespołu'}
            </Description>
          )}

          <div className="mt-4 flex justify-center">
            <span className="font-medium text-[#2E2E38]">{user?.email}</span>
          </div>
        </div>

        <div className="flex w-full flex-col gap-4">
          <ActionButton
            type="button"
            tone="outline"
            size="block"
            onClick={handleResendEmail}
            isLoading={isPending}
            disabled={isPending || cooldown > 0}
          >
            {cooldown > 0
              ? `Wyślij link ponownie (${formatResendCooldown(cooldown)})`
              : 'Wyślij link ponownie'}
          </ActionButton>
        </div>
      </div>
    </AuthFormContainer>
  );
};
